import React from 'react';
import { Bot, Shield, Zap, Activity, Clock } from 'lucide-react';
import { Agent } from '@/types';

interface AgentCardProps extends Agent {
  onClick?: () => void;
}

const tierStyles: Record<string, { bg: string; text: string; border: string }> = {
  Platform:     { bg: 'bg-blue-500/10',   text: 'text-blue-400',   border: 'border-blue-500/20' },
  Channel:      { bg: 'bg-purple-500/10',  text: 'text-purple-400', border: 'border-purple-500/20' },
  Specialized:  { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20' },
  Experimental: { bg: 'bg-orange-500/10',  text: 'text-orange-400', border: 'border-orange-500/20' },
};

const statusColors: Record<string, string> = {
  Active: 'bg-emerald-400',
  Inactive: 'bg-red-400',
  Maintenance: 'bg-amber-400',
  Deprecated: 'bg-gray-400',
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const maturityIcons: Record<string, { icon: any; color: string }> = {
  Production: { icon: Shield,   color: 'text-emerald-400' },
  Beta:       { icon: Zap,      color: 'text-amber-400' },
  Alpha:      { icon: Activity, color: 'text-orange-400' },
  Legacy:     { icon: Clock,    color: 'text-gray-400' },
};

const AgentCard: React.FC<AgentCardProps> = ({ 
  name, tier, status, verdictMaturity, description, tags, lastUpdated, performance, onClick 
}) => {
  const tierStyle = tierStyles[tier] || tierStyles.Specialized;
  const maturity = maturityIcons[verdictMaturity] || maturityIcons.Alpha;
  const MaturityIcon = maturity.icon;

  const formatTime = (dateString: string) => {
    const diff = Math.floor((Date.now() - new Date(dateString).getTime()) / 60000);
    if (diff < 60) return `${diff}m ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)}h ago`;
    return `${Math.floor(diff / 1440)}d ago`;
  };

  return (
    <div 
      className={`
        group relative bg-navy-card border border-navy-border rounded-xl p-5
        transition-all duration-300 ease-out
        hover:border-gold-primary/30 hover:shadow-lg hover:shadow-gold-primary/[0.04] hover:-translate-y-0.5
        ${onClick ? 'cursor-pointer' : ''}
      `}
      onClick={onClick}
      role={onClick ? 'button' : 'article'}
      tabIndex={onClick ? 0 : undefined}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-3">
          <div className="p-2 rounded-lg bg-navy-primary group-hover:bg-gold-primary/5 transition-colors">
            <Bot size={20} className="text-gold-primary" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-text-heading">{name}</h3>
            <div className="flex items-center space-x-2 mt-1">
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${tierStyle.bg} ${tierStyle.text} ${tierStyle.border} border`}>
                {tier}
              </span>
              <div className="flex items-center space-x-1">
                <div className={`w-1.5 h-1.5 rounded-full ${statusColors[status] || 'bg-gray-400'}`} />
                <span className="text-[10px] text-text-muted">{status}</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex items-center space-x-1 px-1.5 py-0.5 bg-navy-primary rounded">
          <MaturityIcon size={12} className={maturity.color} />
          <span className="text-[10px] font-medium text-text-muted">{verdictMaturity}</span>
        </div>
      </div>

      {/* Description */}
      <p className="text-xs text-text-muted leading-relaxed mb-3 line-clamp-2">
        {description}
      </p>

      {/* Tags */}
      {tags && tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {tags.slice(0, 3).map((tag, i) => (
            <span key={i} className="px-1.5 py-0.5 bg-navy-border/40 text-text-muted text-[10px] rounded">
              {tag}
            </span>
          ))}
          {tags.length > 3 && (
            <span className="px-1.5 py-0.5 bg-navy-border/20 text-text-muted/60 text-[10px] rounded">
              +{tags.length - 3}
            </span>
          )}
        </div>
      )}

      {/* Performance */}
      {performance && (
        <div className="mb-3">
          <div className="grid grid-cols-3 gap-2 text-[10px] mb-2">
            <div className="text-center">
              <span className="font-bold text-emerald-400">{performance.reliability}%</span>
              <p className="text-text-muted/60">Reliable</p>
            </div>
            <div className="text-center">
              <span className="font-bold text-blue-400">{performance.efficiency}%</span>
              <p className="text-text-muted/60">Efficient</p>
            </div>
            <div className="text-center">
              <span className="font-bold text-purple-400">{performance.satisfaction}%</span>
              <p className="text-text-muted/60">Satisfact.</p>
            </div>
          </div>
          <div className="space-y-1">
            {[
              { val: performance.reliability, color: 'bg-emerald-400' },
              { val: performance.efficiency, color: 'bg-blue-400' },
              { val: performance.satisfaction, color: 'bg-purple-400' },
            ].map((bar, i) => (
              <div key={i} className="h-[3px] bg-navy-border/50 rounded-full overflow-hidden">
                <div className={`h-full ${bar.color} rounded-full transition-all duration-700`} style={{ width: `${bar.val}%` }} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between text-[10px] text-text-muted/60 pt-3 border-t border-navy-border/50">
        <span>Updated</span>
        <span>{formatTime(lastUpdated)}</span>
      </div>
    </div>
  );
};

export default AgentCard;
